import React from 'react';
import { Alert, Box, Button, Card, Stack, Typography } from '@mui/material';
import { HomeRounded, RefreshRounded, ReportProblemRounded } from '@mui/icons-material';

/**
 * Keeps an isolated page failure from turning the entire workspace white.
 * Changing routes resets the boundary so the rest of the practice remains usable.
 */
export default class WorkspaceErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Preserve diagnostics for the browser console without exposing client data in the UI.
    console.error('Lex Maple page failure', error, info);
  }

  componentDidUpdate(previousProps) {
    if (this.state.error && previousProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <Box role="alert" sx={{ minHeight: '100%', display: 'grid', placeItems: 'center', p: { xs: 2, md: 4 } }}>
        <Card sx={{ width: '100%', maxWidth: 680, p: { xs: 2.5, md: 4 }, borderRadius: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ width: 46, height: 46, borderRadius: 2.5, bgcolor: 'warning.light', color: 'warning.dark', display: 'grid', placeItems: 'center' }}>
              <ReportProblemRounded />
            </Box>
            <Box>
              <Typography variant="h5">This tool needs a fresh start</Typography>
              <Typography color="text.secondary" sx={{ mt: 0.35 }}>Your saved firm and matter records were not changed.</Typography>
            </Box>
          </Stack>
          <Alert severity="info" sx={{ mt: 2.5 }}>
            Reload this tool first. If it still fails, return to the command centre and continue working in another part of the workspace.
          </Alert>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 2.5 }}>
            <Button variant="contained" startIcon={<RefreshRounded />} onClick={() => window.location.reload()}>Reload this tool</Button>
            <Button component="a" href="/dashboard" variant="outlined" startIcon={<HomeRounded />}>Command centre</Button>
          </Stack>
        </Card>
      </Box>
    );
  }
}
